import { Injectable, NgZone, signal, WritableSignal } from '@angular/core';
import { ApiService } from './api.service';
import { Device } from '@twilio/voice-sdk';
import { localStorageService } from './localstorage.service';
import { SharedService } from './shared.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TwilioService {
  public device: Device | null = null;
  callStartTime: number | null = null;
  callDuration: string = '';
  isInCall = false;
  callSid: string = '';
  public incomingCall: any;
  public showIncoming = false;
  parameterData: any;
  call_id: any;
  activeCall: any;
  mutedCall: boolean = false;
  connection: any;
  isCallRejected: WritableSignal<any | null> = signal(null);
  public callended$: BehaviorSubject<any> = new BehaviorSubject(false);
  called: boolean = false;
  extra_data: any = {};
  opencalling$: BehaviorSubject<any> = new BehaviorSubject(false);
  onhold: boolean = false;
  total_duration = "";
  twilioInitiated: boolean = false;
  callactive: boolean = false;
  private refreshInterval: any;
  activeConnection: any = null;  // The call currently in progress
  pendingConnection: any = null; // The call currently ringing/waiting
  isReconnecting = false;        // true while Twilio is recovering a dropped media/signaling link
  callQualityWarning: string | null = null; // active call-quality warning name (e.g. high-packet-loss)
  constructor(
    public _api: ApiService,
    // private modalService: BsModalService,
    public _shared: SharedService,
    private _local: localStorageService,
    private ngZone: NgZone
  ) {}

  disconnectAll() {
    if (this.device) {
      this.device.disconnectAll();
    }
  }

  admin_mobile =
    this._local.customGetItem('admin_profile')?.admin_mobile;
  async initTwilio(adminMobile: any): Promise<void> {
    const role_id = this._local.customGetItem('admin_profile')?.role_id ?? 0;
    if (role_id == 1 || role_id == 4 || role_id == 13) {
      console.log('Twilio device registration skipped for role_id:', role_id);
      return;
    }

    if (!this.admin_mobile && adminMobile){
      this.admin_mobile = adminMobile;
    }
    if (this.admin_mobile) {
      this._api
        .api(
          { phone_number: '+1' + this.admin_mobile },
          'finance/generateAccessToken',
          'get'
        )
        .subscribe((resp) => {
          this.device = new Device(resp.token, {
            enableRingingState: true,
            debug: true,
            allowIncomingWhileBusy: true,
            codecPreferences: ['opus', 'pcmu'], // Opus tolerates packet loss/jitter far better than PCMU, reducing audio dropouts
          } as any);
          if (Device.audioContext?.state === 'suspended') {
            Device.audioContext.resume().catch(() => {
              document.body.addEventListener(
                'click',
                () => Device.audioContext?.resume(),
                { once: true }
              );
            });
          }

          this.device.on('registered', () => {
            this.twilioInitiated = true;
            console.log('✅ Device ready');
          });

          this.device.on('incoming', (conn) => {
            this.ngZone.run(() => {
            console.log('connection established', conn);
            this.parameterData = conn.parameters;
            // this.connection = conn;
            this.activeCall = conn._activeCall;
            this.call_id = conn.parameters.CallSid;
            this.called = true;
            // this.incomingCall = conn;
            this.showIncoming = true;
            this.isCallRejected.set(false);
            this.callactive = true;
            this.callStartTime = Date.now();
            this.pendingConnection = conn;
            this.opencalling$.next(true);
            (this.extra_data = {
              call_id: this.call_id,
              device_data: this.parameterData,
              device: this.device,
            }),

              conn.on('reconnecting', (err: any) => {
                this.ngZone.run(() => {
                  this.isReconnecting = true;
                  console.warn('🔄 Reconnecting incoming call:', err?.code, err?.message);
                });
              });

              conn.on('reconnected', () => {
                this.ngZone.run(() => {
                  this.isReconnecting = false;
                  console.log('✅ Incoming call reconnected');
                });
              });

              conn.on('warning', (name: string) => {
                this.ngZone.run(() => {
                  this.callQualityWarning = name;
                  console.warn('⚠️ Call quality warning:', name);
                });
              });

              conn.on('warning-cleared', (name: string) => {
                this.ngZone.run(() => {
                  if (this.callQualityWarning === name) this.callQualityWarning = null;
                  console.log('✅ Call quality warning cleared:', name);
                });
              });

              conn.on('disconnect', () => {
                console.log('📴 Call ended');
                this.ngZone.run(() => {
                  this.isReconnecting = false;
                  this.callQualityWarning = null;
                  // if (this.activeConnection === conn) this.activeConnection = null;
                  if (!this.activeConnection && this.pendingConnection === conn) {
                    this.pendingConnection = null;
                  } 
                  if (this.pendingConnection && !this.activeConnection) {
                    // this.activeConnection = this.pendingConnection;
                    // this.pendingConnection = null;
                    this.showIncoming = true;
                    this.callDuration = "";

                    this.isCallRejected.set(false);
                    this.callactive = true;
                    // this.isInCall = true;
                    this.callStartTime = Date.now();
                    this.opencalling$.next(true);
                  } else {
                    if(this.activeConnection && this.pendingConnection){
                      this.callDuration = "";
                      this.isInCall = false;
                      this.callactive = true;
                      this.isCallRejected.set(false);
                      this.showIncoming = true;
                      this.callStartTime = Date.now();
                      this.activeConnection = null
                      this.opencalling$.next(true);

                    }else{
                      setTimeout(() => {
                        this.opencalling$.next(false);
                      }, 1000);
                      this.callDuration = "";
                      this.isInCall = false;
                      this.callactive = false;
                      this.isCallRejected.set(true);
                      this.showIncoming = false;

                    }
                  }

                  // this.incomingCall = null;
                  
                  // If no calls left, close the UI
                });
              });

              conn.on('cancel', () => {
                this.ngZone.run(() => {
                  if (this.pendingConnection === conn) this.pendingConnection = null;
                  if (!this.activeConnection){
                    this.isInCall = false;
                    this.isCallRejected.set(true);
                    this.callactive = false;
                    this.showIncoming = false;
                    setTimeout(() => {
                    this.opencalling$.next(false);
                  }, 1000);
                  } 
                });
                console.log('📴 User cancelled the call');
                // this.incomingCall = null;
              });

              // conn.on('accept', () => {
              //   this.ngZone.run(() => {
              //     this.activeConnection = this.pendingConnection;
              //     this.pendingConnection = null;
              //   });
              // });

              // If we already have an active call, this is "Call Waiting"
              // if (this.activeConnection && this.activeConnection.status() === 'open') {
              //   console.warn('Call Waiting: Another call is coming in.');
              //   this.pendingConnection = conn;
              // } else {
              //   // Normal incoming call flow
              //   this.pendingConnection = conn;
              // }
            })
          });

          this.device.on('error', (err: any) => {
            console.error('❌ Twilio Error:', err)
            if (err.code === 20104 || err.code === 31205) {
              console.warn('Critical: Token expired error detected. Forcing immediate refresh.');
              this.refreshTwilioToken();
            }
          });

          this.device.on('tokenWillExpire', async () => {
            this.refreshTwilioToken();
          });

          this.device.on('unregistered', async () => {
            this.device?.register();
          });

          this.device?.register();
        });
        this.startHeartBeat();
    }
  }
  trackDuration() {
  const startTime = Date.now();
  let total_duration = '';

  const interval = setInterval(() => {
    if (!this.isInCall) {
      this.total_duration = this.callDuration;
      clearInterval(interval);
      return;
    }

    const elapsedMs = Date.now() - startTime;
    const totalSeconds = Math.floor(elapsedMs / 1000);

    const minutes = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');

    this.callDuration = `${minutes}:${seconds}`;
  }, 1000);
  total_duration = this.callDuration;
  console.log(total_duration);
}
  acceptCall() {
    this.pendingConnection?.accept();
    this.isInCall = true;
    // this.incomingCall = null;
    this.called = true;
    this.isCallRejected.set(false);
    this.trackDuration();
    this.activeConnection = this.pendingConnection;
    this.pendingConnection = null;
  }
  mute() {
    this.activeConnection?.mute(true);
  }

  unmute() {
    this.activeConnection?.mute(false);
  }

  toggleMute() {
    if (this.activeConnection) {
      this.activeConnection.mute(!this.activeConnection.isMuted());
    }
  }

  isMuted(): boolean {
    return this.activeConnection ? this.activeConnection.isMuted() : false;
  }
  rejectCall() {
    this.isReconnecting = false;
    this.callQualityWarning = null;
    if (!this.activeConnection && this.pendingConnection) {
      this.pendingConnection.reject()
      const data = {
        CallSid: this.pendingConnection.parameters.CallSid,
      };
      this._api.api(data, 'guest/update_twilio_call_status').subscribe((resp) => {
        console.log(resp);
        if (resp) {
          setTimeout(() => {
            this.opencalling$.next(false);
          })
        }
      });
      this.callDuration = '';
      this.showIncoming = false;
      this.isCallRejected.set(true);
      this.isInCall = false;
      this.pendingConnection = null;
    } else {
      this.activeConnection?.reject();
      const data = {
        CallSid: this.activeConnection.parameters.CallSid,
      };
      this._api.api(data, 'guest/update_twilio_call_status').subscribe((resp) => {
        console.log(resp);
        if (resp) {
          setTimeout(() => {
            if (!this.pendingConnection){
              this.opencalling$.next(false);
            }
          })
        }
      });
      this.callDuration = '';
       if (!this.pendingConnection){
         this.isCallRejected.set(true);
        this.showIncoming = false;
       }else{
          this.isCallRejected.set(false);
       }
      this.activeConnection = null;
      this.isInCall = false;
    }
    // this.device?.disconnectAll();
    // this.activeConnection?.disconnect();   
  }
  unregisterDevice() {
    if (this.device) {
      try {
        this.twilioInitiated = false;
        this.device.disconnectAll();
        // this.device.unregister();
        this.device.destroy();
        if(this.device){
          console.warn('not unregistered', this.device)
        }
      } catch (e) {
        console.warn("device unregister error", e);
      }
    }
  }

  // Create a dedicated method for re-usability
  private async refreshTwilioToken() {
    this._api.api({ phone_number: '+1' + this.admin_mobile }, 'finance/generateAccessToken', 'get')
      .subscribe(async (resp: any) => {
        try {
          // 1. Update the token
          if (this.device){
            this.device?.updateToken(resp.token);
            console.log('Twilio Device re-registered with new token.');
          }else {
            console.warn("device not found while updating token");
          }

          // 2. Explicitly ensure the device is registered for incoming calls
          // In newer SDK versions, updateToken handles this, but a manual 
          // .register() ensures the signaling channel is refreshed.
          // this.device?.register();

        } catch (err) {
          console.error('Failed to update Twilio token:', err);
        }
      });
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payloadBase64 = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payloadBase64));

      // Twilio 'exp' is in seconds, Date.now() is in milliseconds
      const expiryTime = decodedPayload.exp * 1000;
      const currentTime = Date.now();

      // Add a 1-minute "buffer" to be safe
      return currentTime + 60000 >= expiryTime;
    } catch (e) {
      return true; // Assume expired if decode fails
    }
  }

  private startHeartBeat(): void {
    // 2. Start the Heartbeat (Checks every 1 minute)
      if (this.refreshInterval) clearInterval(this.refreshInterval);

      this.refreshInterval = setInterval(() => {
        const currentToken = this.device?.token;
        if (!currentToken || this.isTokenExpired(currentToken)) {
          console.warn("Heartbeat: Token is expired or missing. Refreshing...");
          this.refreshTwilioToken();
        }
      }, 60000);
  }

  acceptAndDropCurrent() {
    if (this.activeConnection) {
      this.activeConnection.disconnect(); // Hang up current
    }

    if (this.pendingConnection) {
      this.pendingConnection.accept(); // Answer waiting call
      // Note: the 'accept' listener above will move pending to active
    }
  }

  /**
   * Action: Reject the waiting call without affecting the current one
   */
  rejectWaitingCall() {
    if (this.pendingConnection) {
      this.pendingConnection.reject();
      this.pendingConnection = null;
    }
  }
}
